// Supabase client configuration
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// 전역 변수로 Supabase 클라이언트를 한 번만 생성
// window 객체에 저장하여 중복 생성 방지
if (!window.__supabaseClient) {
  window.__supabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      storage: window.localStorage,
      storageKey: 'supabase.auth.token',
      flowType: 'pkce'
    }
  });
}

export const supabase = window.__supabaseClient;

// Database type definitions for TypeScript (optional but recommended)
export interface Profile {
  id: string;
  email: string;
  name?: string;
  avatar_url?: string;
  role?: 'user' | 'admin';
  created_at: string;
  updated_at: string;
}

export interface ConsultingSession {
  id: string;
  user_id: string;
  name: string;
  status: 'active' | 'completed' | 'paused' | 'abandoned';
  current_step: string;
  progress_percentage: number;
  system_prompt_id?: number;
  session_data?: any;
  user_satisfaction_score?: number;
  session_duration_minutes?: number;
  total_messages_count?: number;
  completion_rate?: number;
  started_at: string;
  completed_at?: string;
  last_activity_at: string;
  created_at: string;
  updated_at: string;
}

export interface ChatMessage {
  id: string;
  session_id: string;
  sender: 'user' | 'ai';
  content: string;
  message_type: 'text' | 'voice' | 'system';
  is_typing: boolean;
  created_at: string;
}

export interface Resume {
  id: string;
  session_id: string;
  user_id: string;
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  github?: string;
  title?: string;
  summary?: string;
  key_strengths: string[];
  experiences: Experience[];
  education: Education[];
  certificates: Certificate[];
  skills: string[];
  papers?: Paper[];
  social_links?: SocialLink[];
  english_version?: any; // JSON object for English version
  currently_updating_field?: string;
  version: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Experience {
  company: string;
  position: string;
  startDate: string;
  endDate: string;
  overallDescription?: string;
  projects?: Project[];
}

export interface Project {
  projectName: string;
  projectDescription: string;
}

export interface Education {
  degree: string;
  school: string;
  major?: string;
  startDate: string;
  endDate: string;
}

export interface Certificate {
  name: string;
  issuer: string;
  date: string;
}

export interface Paper {
  title: string;
  authors?: string[];
  journal?: string;
  date?: string;
  url?: string;
}

export interface SocialLink {
  platform: string;
  url: string;
  icon?: string;
}

export interface CoverLetter {
  id: string;
  resume_id?: string;
  user_id: string;
  title?: string;
  content: {
    introduction?: string;
    strengths?: string;
    achievements?: string;
    values?: string;
  };
  version: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SystemPrompt {
  id: number;
  name: string;
  version: string;
  content: string;
  is_active: boolean;
  target_user_group: string;
  metadata?: any;
  description?: string;
  total_sessions?: number;
  avg_completion_rate?: number;
  avg_satisfaction_score?: number;
  avg_session_duration_minutes?: number;
  created_by?: string;
  created_at: string;
  activated_at?: string;
  deactivated_at?: string;
}

export interface AILog {
  id: string;
  session_id?: string;
  message_id?: string; // References the AI chat message
  log_type: 'request' | 'response' | 'error' | 'simulation';
  user_message?: string;
  ai_response?: any;
  processing_time_ms?: number;
  current_step?: string;
  api_used?: string;
  token_count?: number;
  error_message?: string;
  system_prompt_id?: number;
  prompt_version?: string;
  created_at: string;
}

// Portfolio related interfaces
export interface Portfolio {
  id: string;
  user_id: string;
  session_id?: string;
  slug: string;
  status: 'published' | 'draft';
  resume_snapshot?: any; // JSONB
  theme: string;
  theme_config?: any; // JSONB
  custom_domain?: string;
  profile_image_url?: string;
  original_image_url?: string;
  ai_enhancement_data?: {
    gfpgan_url?: string;
    background_remover_url?: string;
    final_url?: string;
    selected_type?: string;
  };
  view_count: number;
  created_at: string;
  updated_at?: string;
}

export interface PortfolioPost {
  id: string;
  portfolio_id: string;
  experience_id?: string;
  type: string;
  title: string;
  content_markdown?: string;
  cover_image_url?: string;
  status: 'published' | 'draft';
  view_count: number;
  created_at: string;
  updated_at?: string;
}

export interface UserAction {
  id: string;
  user_id?: string;
  session_id?: string;
  resume_id?: string;
  portfolio_id?: string;
  action_type: 'pdf_download' | 'portfolio_create' | 'resume_view' | 'portfolio_view';
  action_details?: any; // JSONB
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

export interface PDFDownload {
  id: string;
  user_id: string;
  session_id: string;
  resume_id?: string;
  template_id: number;
  template_name?: string;
  download_language: 'kr' | 'en';
  browser_info?: any; // JSONB
  created_at: string;
}
